import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Flame, CheckCircle2, Activity, Calendar, Sprout, Medal, Trophy, Lock, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const BADGE_ICON = { sprout: Sprout, flame: Flame, medal: Medal, trophy: Trophy };
const BADGE_TONE = {
  bronze:   "from-amber-600/20 to-amber-800/10 text-amber-700 border-amber-600/40",
  silver:   "from-slate-300/30 to-slate-500/10 text-slate-600 border-slate-400/50",
  gold:     "from-yellow-400/30 to-amber-500/10 text-amber-600 border-yellow-500/50",
  platinum: "from-primary/30 to-accent/10 text-primary border-primary/50",
};

/**
 * Historial Diario — patient training timeline + streak + badges.
 * Data source: GET /api/progress/timeline?days=60
 */
export default function History() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(60);
  const [celebration, setCelebration] = useState(null); // array of newly earned badges

  useEffect(() => {
    api.get(`/progress/timeline?days=${days}`).then((r) => {
      setData(r.data);
      if ((r.data.new_badges || []).length > 0 && !celebration) {
        setCelebration(r.data.new_badges);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const grid = useMemo(() => buildGrid(data?.days || [], days), [data, days]);

  const acknowledgeBadges = async () => {
    try {
      await api.post("/progress/badges/ack");
    } catch { /* ignore */ }
    setCelebration(null);
    // Refresh so is_new flags update
    const r = await api.get(`/progress/timeline?days=${days}`);
    setData(r.data);
  };

  if (!data) return <p className="p-8">Cargando historial...</p>;

  const nextBadge = (data.badges || []).find((b) => !b.achieved);

  return (
    <div className="space-y-8" data-testid="history-page">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Constancia</p>
        <h1 className="heading text-4xl sm:text-5xl font-extrabold mt-1 tracking-tight">Mi historial</h1>
        <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
          Línea de tiempo con lo que has entrenado cada día y tu racha activa.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat
          testid="stat-streak"
          icon={<Flame size={22} />}
          label="Racha actual"
          value={`${data.streak} ${data.streak === 1 ? "día" : "días"}`}
          hint={data.streak > 0 ? "¡No la rompas!" : "Empieza hoy mismo"}
          highlight={data.streak > 0}
        />
        <Stat
          testid="stat-best"
          icon={<CheckCircle2 size={22} />}
          label="Mejor racha"
          value={`${data.best_streak} ${data.best_streak === 1 ? "día" : "días"}`}
          hint="En esta ventana"
        />
        <Stat
          testid="stat-attempts"
          icon={<Activity size={22} />}
          label="Ejercicios"
          value={data.total_attempts}
          hint={`Últimos ${data.window_days} días`}
        />
        <Stat
          testid="stat-accuracy"
          icon={<Activity size={22} />}
          label="Acierto global"
          value={`${Math.round((data.overall_accuracy || 0) * 100)}%`}
          hint={`${data.active_days} días activos`}
        />
      </div>

      {/* Badges */}
      <Card className="rounded-2xl border-border" data-testid="badges-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-primary" />
              <span className="heading font-bold">Medallas de constancia</span>
            </div>
            {nextBadge && (
              <div className="text-xs text-foreground/70" data-testid="next-badge-hint">
                Próxima medalla: <b>{nextBadge.label}</b> en{" "}
                <b>{Math.max(0, nextBadge.threshold - Math.max(data.streak, data.best_streak))}</b>{" "}
                {nextBadge.threshold - Math.max(data.streak, data.best_streak) === 1 ? "día" : "días"}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.badges || []).map((b) => {
              const Icon = BADGE_ICON[b.icon] || Medal;
              const tone = BADGE_TONE[b.color] || BADGE_TONE.bronze;
              const cls = b.achieved
                ? `bg-gradient-to-br ${tone}`
                : "bg-secondary/60 text-foreground/50 border-border";
              return (
                <div
                  key={b.code}
                  data-testid={`badge-${b.code}`}
                  className={`rounded-2xl border p-4 flex flex-col items-center text-center transition-all ${cls} ${b.achieved ? "shadow-sm" : ""}`}
                >
                  <div className="w-12 h-12 rounded-full grid place-items-center border border-current/40 mb-2 relative">
                    {b.achieved ? <Icon size={22} /> : <Lock size={20} />}
                    {b.achieved && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground grid place-items-center">
                        <CheckCircle2 size={12} />
                      </span>
                    )}
                  </div>
                  <div className="heading font-extrabold text-sm">{b.label}</div>
                  <div className="text-[11px] uppercase tracking-widest mt-1">{b.threshold} días</div>
                  {b.achieved && (
                    <div className="text-[11px] mt-2 leading-snug opacity-90">{b.message}</div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Heatmap-style calendar */}
      <Card className="rounded-2xl border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-primary" />
              <span className="heading font-bold">Calendario de constancia</span>
            </div>
            <div className="flex gap-2 text-xs">
              {[30, 60, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`history-range-${d}`}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1 rounded-full border transition-colors ${
                    days === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"
                  }`}
                >{d} días</button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="inline-grid gap-1" style={{ gridTemplateRows: "repeat(7, 14px)", gridAutoFlow: "column" }}>
              {grid.map((cell, i) => (
                <div
                  key={i}
                  title={cell.title}
                  className={`w-3.5 h-3.5 rounded-sm ${cell.className}`}
                  data-testid={cell.date ? `history-cell-${cell.date}` : undefined}
                />
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <span>Menos</span>
            <div className="w-3 h-3 rounded-sm bg-secondary" />
            <div className="w-3 h-3 rounded-sm bg-primary/25" />
            <div className="w-3 h-3 rounded-sm bg-primary/55" />
            <div className="w-3 h-3 rounded-sm bg-primary/85" />
            <div className="w-3 h-3 rounded-sm bg-primary" />
            <span>Más</span>
          </div>
        </CardContent>
      </Card>

      {/* Timeline list */}
      <div>
        <h2 className="heading text-xl font-bold mb-3">Últimos días activos</h2>
        {data.days.length === 0 ? (
          <Card className="rounded-2xl border-border">
            <CardContent className="p-6 text-foreground/70">
              Aún no has completado ningún ejercicio en esta ventana. Empieza con "Mi entrenamiento de hoy".
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="history-timeline">
            {[...data.days].reverse().slice(0, 14).map((d) => (
              <motion.div
                key={d.date}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-4"
                data-testid={`history-day-${d.date}`}
              >
                <div className="w-14 shrink-0">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{formatDay(d.date)}</div>
                  <div className="heading text-lg font-extrabold">{formatDate(d.date)}</div>
                </div>
                <div className="flex-1 flex flex-wrap gap-2">
                  {d.domains.map((dom) => (
                    <Badge key={dom} className="bg-accent/15 text-accent capitalize">{dom.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
                <div className="text-sm text-foreground/80">
                  <span className="font-bold">{d.total}</span> ejercicios · <span className="font-bold">{Math.round(d.accuracy * 100)}%</span> acierto
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Celebration dialog for newly earned badges */}
      <Dialog open={!!celebration} onOpenChange={(o) => !o && acknowledgeBadges()}>
        <DialogContent data-testid="badge-celebration">
          <DialogHeader>
            <DialogTitle className="heading text-2xl flex items-center gap-2">
              <Sparkles className="text-primary" size={22} />
              {celebration && celebration.length > 1 ? "¡Nuevas medallas!" : "¡Nueva medalla!"}
            </DialogTitle>
            <DialogDescription>
              Has desbloqueado {celebration?.length === 1 ? "una medalla" : `${celebration?.length} medallas`} de constancia.
              Sigue así, cada día cuenta.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
            {(celebration || []).map((b) => {
              const Icon = BADGE_ICON[b.icon] || Medal;
              const tone = BADGE_TONE[b.color] || BADGE_TONE.bronze;
              return (
                <motion.div
                  key={b.code}
                  initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 220, damping: 15 }}
                  className={`rounded-2xl border p-4 text-center bg-gradient-to-br ${tone}`}
                >
                  <div className="w-12 h-12 mx-auto rounded-full border border-current/40 grid place-items-center mb-2">
                    <Icon size={22} />
                  </div>
                  <div className="heading font-extrabold">{b.label}</div>
                  <div className="text-[11px] uppercase tracking-widest">{b.threshold} días</div>
                  <div className="text-xs mt-2 leading-snug">{b.message}</div>
                </motion.div>
              );
            })}
          </div>
          <DialogFooter>
            <Button className="rounded-full h-11 px-6" onClick={acknowledgeBadges} data-testid="badge-celebrate-ack">
              ¡Genial, seguir así!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon, label, value, hint, testid, highlight }) {
  return (
    <Card className={`rounded-2xl ${highlight ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className={highlight ? "text-primary" : "text-foreground/60"}>{icon}</span>
          <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
        </div>
        <div className="heading text-3xl font-extrabold mt-2">{value}</div>
        <div className="text-xs text-foreground/60 mt-1">{hint}</div>
      </CardContent>
    </Card>
  );
}

function buildGrid(days, windowDays) {
  const map = new Map(days.map((d) => [d.date, d]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(today.getDate() - (windowDays - 1));
  // Align to Monday of that week for a clean 7-row grid
  const dayOfWeek = (start.getDay() + 6) % 7; // Mon=0
  start.setDate(start.getDate() - dayOfWeek);
  const cells = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const iso = cursor.toISOString().slice(0, 10);
    const beforeWindow = cursor < new Date(today.getTime() - (windowDays - 1) * 86400000);
    const d = map.get(iso);
    let className = "bg-secondary";
    if (beforeWindow) className = "bg-transparent";
    else if (d) {
      const intensity = Math.min(1, d.total / 15);
      if (intensity >= 0.8) className = "bg-primary";
      else if (intensity >= 0.5) className = "bg-primary/85";
      else if (intensity >= 0.25) className = "bg-primary/55";
      else className = "bg-primary/25";
    }
    cells.push({
      date: beforeWindow ? null : iso,
      className,
      title: d ? `${iso} · ${d.total} ejercicios (${Math.round(d.accuracy * 100)}%)` : iso,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return cells;
}

function formatDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-ES", { weekday: "short" });
}

function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
}
